import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Calendar, Clock, Target, TrendingUp, BookOpen, ArrowLeft, Sparkles, RefreshCw, Loader2 } from "lucide-react";
import { format, differenceInDays } from "date-fns";
import { useToast } from "@/components/ui/use-toast";
import { AICoachInsights } from "@/components/study-plan/AICoachInsights";
import { CalendarExportButton } from "@/components/study-plan/CalendarExportButton";

export default function StudyPlanDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const planId = searchParams.get('planId');
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: studyPlan, isLoading } = useQuery({
    queryKey: ['study-plan', planId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('study_plans')
        .select(`
          *,
          study_plan_weeks (
            *,
            study_plan_tasks (*)
          )
        `)
        .eq('id', planId!)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!planId
  });

  const { data: activePlan } = useQuery({
    queryKey: ['active-study-plan', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('study_plans')
        .select(`
          *,
          study_plan_weeks (
            *,
            study_plan_tasks (*)
          )
        `)
        .eq('user_id', user!.id)
        .eq('status', 'active')
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      return data;
    },
    enabled: !!user && !planId
  });

  const plan = planId ? studyPlan : activePlan;

  if (isLoading) {
    return (
      <div className="container mx-auto py-8 px-4">
        <div className="flex items-center justify-center min-h-[400px]">
          <p className="text-muted-foreground">Loading study plan...</p>
        </div>
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="container mx-auto py-8 px-4">
        <Card>
          <CardContent className="py-8 text-center">
            <BookOpen className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-lg font-semibold mb-2">No Active Study Plan</h3>
            <p className="text-muted-foreground mb-4">
              Create a personalized study plan to get started
            </p>
            <Button onClick={() => navigate('/study-plan/setup')}>
              Create Study Plan
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const daysUntilExam = differenceInDays(new Date(plan.exam_date), new Date());
  const currentWeek = plan.study_plan_weeks?.find(w => w.status === 'active');
  const completedWeeks = plan.study_plan_weeks?.filter(w => w.status === 'completed').length || 0;
  const totalWeeks = plan.study_plan_weeks?.length || 0;
  const overallProgress = Math.round((completedWeeks / totalWeeks) * 100);

  const todaysTasks = currentWeek?.study_plan_tasks?.filter(
    t => t.scheduled_date === format(new Date(), 'yyyy-MM-dd')
  ) || [];

  const thisWeekProgress = currentWeek?.completion_percentage || 0;
  
  // Fetch latest AI coach insights - defined after currentWeek
  const { data: latestInsights } = useQuery({
    queryKey: ['ai-coach-insights', plan?.id, currentWeek?.week_number],
    queryFn: async () => {
      if (!plan?.id || !currentWeek?.week_number) return null;
      
      const { data, error } = await supabase
        .from('ai_coach_insights')
        .select('*')
        .eq('plan_id', plan.id)
        .eq('week_number', currentWeek.week_number)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      
      if (error && error.code !== 'PGRST116') throw error;
      return data;
    },
    enabled: !!plan && !!currentWeek
  });
  
  // Request new coaching check-in
  const requestCheckinMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke('ai-weekly-coach-checkin', {
        body: {
          planId: plan!.id,
          weekNumber: currentWeek!.week_number
        }
      });
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-coach-insights'] });
      toast({
        title: "Fresh insights generated! ✨",
        description: "Your AI coach has analyzed your latest progress."
      });
    },
    onError: (error) => {
      toast({
        title: "Failed to generate insights",
        description: error instanceof Error ? error.message : "Please try again later.",
        variant: "destructive"
      });
    }
  });

  return (
    <div className="container mx-auto py-8 px-4">
      {/* Header */}
      <div className="mb-6">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">My Study Plan</h1>
            <p className="text-muted-foreground">
              {daysUntilExam} days until exam
            </p>
          </div>
          <div className="flex items-center gap-2">
            <CalendarExportButton plan={plan} examLabel={(plan as any).exam_slug || undefined} />
            <Badge variant={daysUntilExam < 14 ? "destructive" : "default"}>
              <Calendar className="h-3 w-3 mr-1" />
              {format(new Date(plan.exam_date), 'MMM dd, yyyy')}
            </Badge>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              This Week
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">Week {currentWeek?.week_number || 1} of {totalWeeks}</div>
            <p className="text-sm text-muted-foreground mt-1">
              Focus: {(currentWeek?.focus_topics as any)?.[0]?.topicName || 'Loading...'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Progress
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{overallProgress}% Complete</div>
            <div className="flex items-center gap-2 mt-2">
              <Progress value={overallProgress} className="flex-1" />
              {overallProgress >= 75 ? (
                <span className="text-xs text-green-600">On Track ✓</span>
              ) : (
                <span className="text-xs text-muted-foreground">In Progress</span>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Next Milestone
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {(plan.ai_generation_metadata as any)?.milestones?.[0]?.milestone || 'Mock Exam'}
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              in {(plan.ai_generation_metadata as any)?.milestones?.[0]?.week || 4} weeks
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <Tabs defaultValue="today" className="space-y-4">
        <TabsList>
          <TabsTrigger value="today">Today's Schedule</TabsTrigger>
          <TabsTrigger value="week">This Week</TabsTrigger>
          <TabsTrigger value="overview">Overview</TabsTrigger>
        </TabsList>

        <TabsContent value="today" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Today's Tasks</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {todaysTasks.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">
                  No tasks scheduled for today. Enjoy your rest day!
                </p>
              ) : (
                todaysTasks.map((task) => (
                  <div key={task.id} className="border rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Clock className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm font-medium capitalize">
                            {task.scheduled_time_slot} ({task.duration_minutes} min)
                          </span>
                        </div>
                        <h4 className="font-semibold mb-1">
                          {task.task_type === 'practice_mcqs' ? 'Practice MCQs' :
                           task.task_type === 'review_notes' ? 'Review Notes' :
                           task.task_type === 'mock_exam' ? 'Mock Exam' : 'Rest'}
                        </h4>
                        {task.target_mcq_count && (
                          <p className="text-sm text-muted-foreground">
                            Target: {task.target_mcq_count} questions
                          </p>
                        )}
                        {task.tags && task.tags.length > 0 && (
                          <div className="flex gap-2 mt-2">
                            {task.tags.map((tag, idx) => (
                              <Badge key={idx} variant="secondary">{tag}</Badge>
                            ))}
                          </div>
                        )}
                      </div>
                      <Badge variant={
                        task.status === 'completed' ? 'default' :
                        task.status === 'in_progress' ? 'secondary' : 'outline'
                      }>
                        {task.status}
                      </Badge>
                    </div>
                    {task.status === 'pending' && (
                      <Button className="w-full mt-4" onClick={() => navigate('/practice')}>
                        Start Now
                      </Button>
                    )}
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="week" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Weekly Progress</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day, idx) => {
                  const dayTasks = currentWeek?.study_plan_tasks?.filter(
                    t => new Date(t.scheduled_date).getDay() === idx
                  ) || [];
                  const completedTasks = dayTasks.filter(t => t.status === 'completed').length;
                  const progress = dayTasks.length > 0 ? (completedTasks / dayTasks.length) * 100 : 0;

                  return (
                    <div key={day} className="flex items-center gap-4">
                      <div className="w-24 text-sm font-medium capitalize">{day}</div>
                      <Progress value={progress} className="flex-1" />
                      <div className="text-sm text-muted-foreground">
                        {Math.round(progress)}%
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="overview" className="space-y-4">
          {/* AI Coach Insights */}
          {latestInsights ? (
            <>
              <AICoachInsights
                insights={latestInsights.insights as any}
                metrics={latestInsights.metrics as any}
                weekNumber={latestInsights.week_number}
                planId={plan.id}
                weekId={currentWeek?.id}
                insightId={latestInsights.id}
                canApply={currentWeek?.status === 'active'}
                onApplySuccess={() => {
                  queryClient.invalidateQueries({ queryKey: ['study-plan', planId] });
                  queryClient.invalidateQueries({ queryKey: ['active-study-plan'] });
                }}
              />
              <div className="flex justify-center">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => requestCheckinMutation.mutate()}
                  disabled={requestCheckinMutation.isPending}
                >
                  {requestCheckinMutation.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Request New Check-in
                    </>
                  )}
                </Button>
              </div>
            </>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <Sparkles className="h-12 w-12 mx-auto mb-3 text-muted-foreground opacity-50" />
                <h3 className="font-semibold mb-2">AI Coach Insights Coming Soon</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Complete more tasks this week to receive personalized insights
                </p>
                <Progress value={thisWeekProgress} className="max-w-xs mx-auto mb-4" />
                <p className="text-xs text-muted-foreground">
                  {thisWeekProgress}% of this week's tasks completed
                </p>
                {thisWeekProgress >= 50 && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-4"
                    onClick={() => requestCheckinMutation.mutate()}
                    disabled={requestCheckinMutation.isPending}
                  >
                    {requestCheckinMutation.isPending ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Sparkles className="h-4 w-4 mr-2" />
                        Generate Insights Now
                      </>
                    )}
                  </Button>
                )}
              </CardContent>
            </Card>
          )}
          
          {/* Original AI Strategy */}
          <Card>
            <CardHeader>
              <CardTitle>Original Study Strategy</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <TrendingUp className="h-5 w-5 text-primary mt-0.5" />
                  <div>
                    <h4 className="font-semibold mb-1">Overall Strategy</h4>
                    <p className="text-sm text-muted-foreground">
                      {(plan.ai_generation_metadata as any)?.overallStrategy || 'Focus on weak areas while maintaining strong performance in other topics.'}
                    </p>
                  </div>
                </div>
                {(plan.ai_generation_metadata as any)?.recommendations?.map((rec: string, idx: number) => (
                  <div key={idx} className="flex items-start gap-3">
                    <Target className="h-5 w-5 text-primary mt-0.5" />
                    <p className="text-sm text-muted-foreground">{rec}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
